import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

/**
 * realtime gateway（03 §11.3b）のブラウザ側の契約。接続の実装は apps/web/src/api/realtime.ts（1 ユーザー 1 接続・再接続・差分）。
 * modules/*\/ui はここだけを見る: useRealtime() で状態と購読、useRealtimeMessages() でチャネルを絞って受け取る。
 * Provider が無い（テスト・ストーリー）ときは「接続なし」の無害な値を返す。
 */

/** core の RealtimeChannel（@saas/module-core/api）と同じ値。ui はサーバ側の型を import しないので写しを持つ */
export type RealtimeChannelName = 'dm' | 'channel' | 'record' | 'record_signal' | 'presence' | 'notify' | 'telephony' | 'meeting' | 'task';

/** サーバが流す 1 メッセージ（core の RealtimeService が publish した JSON そのもの） */
export interface RealtimeEvent {
  id: string;
  channel: RealtimeChannelName;
  type: string;
  payload: unknown;
  tenantId: string;
  at: string;
}

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface RealtimeBus {
  status: RealtimeStatus;
  /** 全チャネルのメッセージを受け取る。返り値で購読解除 */
  subscribe(handler: (e: RealtimeEvent) => void): () => void;
  /** 再接続の差分が取り切れなかった（全部読み直しが必要）ときに 1 回呼ばれる購読 */
  onResync(handler: () => void): () => void;
  /**
   * 同時編集の部屋（ドキュメント単位・apps/realtime の doc-rooms.ts）。任意: 無い実装（テスト・古い画面）では同時編集を使わない。
   * send: 上りのフレーム（先頭のキーが "doc" の JSON）を送る。接続が開いていなければ false
   * subscribeDocFrames: realtime からの部屋のフレーム（{ doc: 'joined' | 'msg' | … }）を受け取る。返り値で購読解除
   */
  send?(frame: { doc: string; [key: string]: unknown }): boolean;
  subscribeDocFrames?(handler: (frame: Record<string, unknown>) => void): () => void;
}

const NO_BUS: RealtimeBus = { status: 'idle', subscribe: () => () => {}, onResync: () => () => {} };

const RealtimeContext = createContext<RealtimeBus>(NO_BUS);

export function RealtimeProvider({ value, children }: { value: RealtimeBus; children: ReactNode }) {
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/** 接続の状態と購読。Provider が無ければ status = 'idle'（購読しても何も来ない） */
export function useRealtime(): RealtimeBus {
  return useContext(RealtimeContext);
}

/**
 * チャネルを絞ってメッセージを受け取る。handler は最新のものが呼ばれる（依存配列に入れなくてよい）。
 *   useRealtimeMessages(['dm', 'channel'], (e) => { if (e.type === 'message.posted') refetch(); });
 */
export function useRealtimeMessages(channels: readonly RealtimeChannelName[] | null, handler: (e: RealtimeEvent) => void): void {
  const bus = useRealtime();
  const latest = useRef(handler);
  latest.current = handler;
  const key = channels ? channels.join(',') : '*';
  useEffect(() => {
    const wanted = key === '*' ? null : new Set(key.split(',').filter((c) => c.length > 0));
    return bus.subscribe((e) => {
      if (wanted && !wanted.has(e.channel)) return;
      latest.current(e);
    });
  }, [bus, key]);
}

/** 差分の取りこぼし（hello.complete = false）で全部読み直すためのフック */
export function useRealtimeResync(handler: () => void): void {
  const bus = useRealtime();
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => bus.onResync(() => latest.current()), [bus]);
}
